import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  IconButton,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from '@socialchorus/shared-ui-components';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { programSelectors } from '../../../../models/program';
import useImageUploader, {
  MAX_IMAGES,
  MAX_IMAGE_SIZE,
  type UploaderError,
} from './use-image-uploader';
import useVideoUploader, { MAX_VIDEOS } from './use-video-uploader';
import useFileUploader, { MAX_FILES, MAX_FILE_SIZE } from './use-file-uploader';
import useLinkUploader from './use-link-uploader';
import Images from './images';
import Videos from './videos';
import Files from './files';
import Links from './links';
import LinkInput from './link-input';
import { SortableList, SortableGroupWrapper } from './sortable';
import type {
  MediaGroup,
  MediaGroupType,
} from '../../../../models/user-generated-contents/types';
import './post-media.scss';

function mediaGroupOrder(attachments: MediaGroup[]): MediaGroupType[] {
  return attachments
    .filter(
      (
        g
      ): g is Extract<
        MediaGroup,
        { type: 'images' | 'videos' | 'files' | 'links' }
      > =>
        g.type === 'images' ||
        g.type === 'videos' ||
        g.type === 'files' ||
        g.type === 'links'
    )
    .sort((a, b) => a.order - b.order)
    .map((g) => g.type);
}

function resolveVisibleOrder(
  storedOrder: MediaGroupType[],
  hasImages: boolean,
  hasVideos: boolean,
  hasFiles: boolean,
  hasLinks: boolean
): MediaGroupType[] {
  const visible = storedOrder.filter((g) => {
    if (g === 'images') return hasImages;
    if (g === 'videos') return hasVideos;
    if (g === 'files') return hasFiles;
    if (g === 'links') return hasLinks;
    return false;
  });
  if (hasImages && !visible.includes('images')) visible.push('images');
  if (hasVideos && !visible.includes('videos')) visible.push('videos');
  if (hasFiles && !visible.includes('files')) visible.push('files');
  if (hasLinks && !visible.includes('links')) visible.push('links');
  return visible;
}

function applyGroupOrder(
  attachments: MediaGroup[],
  order: MediaGroupType[]
): MediaGroup[] {
  return attachments.map((g) => {
    const idx = order.indexOf(g.type as MediaGroupType);
    return idx >= 0 ? { ...g, order: idx } : g;
  });
}

export const MAX_MEDIA_FILES = MAX_IMAGES + MAX_VIDEOS + MAX_FILES;

export type PostMediaHandle = {
  handleDropAccepted: (files: File[]) => void;
  handleDropRejected: (rejections: FileRejection[]) => void;
};

const PostMedia = forwardRef<PostMediaHandle>(function PostMedia(_, ref) {
  const { t } = useTranslation();
  const { userGeneratedContent, updateUserGeneratedContentFormState } =
    useUserGeneratedContent();
  const images = useImageUploader();
  const videos = useVideoUploader();
  const fileUploader = useFileUploader();
  const linkUploader = useLinkUploader();

  const imagesAttachmentEnabled = usePatronSelector(
    programSelectors.getUserGeneratedContentImagesEnabled
  );
  const videosAttachmentEnabled = usePatronSelector(
    programSelectors.getUserGeneratedContentVideosEnabled
  );
  const filesAttachmentEnabled = usePatronSelector(
    programSelectors.getUserGeneratedContentFilesEnabled
  );
  const linksAttachmentEnabled = usePatronSelector(
    programSelectors.getUserGeneratedContentLinksEnabled
  );
  const showAddToPostToolbar =
    imagesAttachmentEnabled ||
    videosAttachmentEnabled ||
    filesAttachmentEnabled ||
    linksAttachmentEnabled;

  const clearAllErrors = useCallback(() => {
    images.clearErrors();
    videos.clearErrors();
    fileUploader.clearErrors();
    linkUploader.clearErrors();
  }, [
    images.clearErrors,
    videos.clearErrors,
    fileUploader.clearErrors,
    linkUploader.clearErrors,
  ]);

  const handleDropAccepted = useCallback(
    (files: File[]) => {
      clearAllErrors();
      // Disabled attachment types can't be added via the toolbar buttons;
      // silently drop the same file types here so drag-and-drop can't
      // bypass that restriction.
      const imageFiles = imagesAttachmentEnabled
        ? files.filter((f) => f.type.startsWith('image/'))
        : [];
      const videoFiles = videosAttachmentEnabled
        ? files.filter((f) => f.type.startsWith('video/'))
        : [];
      const otherFiles = filesAttachmentEnabled
        ? files.filter(
            (f) => !f.type.startsWith('image/') && !f.type.startsWith('video/')
          )
        : [];

      const oversizedImages = imageFiles.filter((f) => f.size > MAX_IMAGE_SIZE);
      const validImages = imageFiles.filter((f) => f.size <= MAX_IMAGE_SIZE);
      const oversizedVideos = videoFiles.filter(
        (f) => f.size > videos.maxSizeBytes
      );
      const validVideos = videoFiles.filter(
        (f) => f.size <= videos.maxSizeBytes
      );
      const oversizedFiles = otherFiles.filter((f) => f.size > MAX_FILE_SIZE);
      const validFiles = otherFiles.filter((f) => f.size <= MAX_FILE_SIZE);

      if (oversizedImages.length > 0)
        images.handleDropRejected(
          oversizedImages.map((file) => ({
            file,
            errors: [{ code: 'file-too-large', message: '' }],
          }))
        );
      if (oversizedVideos.length > 0)
        videos.handleDropRejected(
          oversizedVideos.map((file) => ({
            file,
            errors: [{ code: 'file-too-large', message: '' }],
          }))
        );
      if (oversizedFiles.length > 0)
        fileUploader.handleDropRejected(
          oversizedFiles.map((file) => ({
            file,
            errors: [{ code: 'file-too-large', message: '' }],
          }))
        );

      if (validImages.length > 0) images.handleDropAccepted(validImages);
      if (validVideos.length > 0) videos.handleDropAccepted(validVideos);
      if (validFiles.length > 0) fileUploader.handleDropAccepted(validFiles);
    },
    [
      clearAllErrors,
      imagesAttachmentEnabled,
      videosAttachmentEnabled,
      filesAttachmentEnabled,
      images.handleDropAccepted,
      images.handleDropRejected,
      videos.handleDropAccepted,
      videos.handleDropRejected,
      videos.maxSizeBytes,
      fileUploader.handleDropAccepted,
      fileUploader.handleDropRejected,
    ]
  );

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      // Mirror handleDropAccepted: a disabled attachment type shouldn't
      // surface its "accepted formats" error either, since none of its
      // formats are actually accepted while the type is disabled.
      const imageRejections: FileRejection[] = [];
      const videoRejections: FileRejection[] = [];
      const fileRejections: FileRejection[] = [];
      rejections.forEach((r) => {
        if (r.file.type.startsWith('image/')) {
          if (imagesAttachmentEnabled) imageRejections.push(r);
        } else if (r.file.type.startsWith('video/')) {
          if (videosAttachmentEnabled) videoRejections.push(r);
        } else if (filesAttachmentEnabled) {
          fileRejections.push(r);
        }
      });
      if (imageRejections.length > 0)
        images.handleDropRejected(imageRejections);
      if (videoRejections.length > 0)
        videos.handleDropRejected(videoRejections);
      if (fileRejections.length > 0)
        fileUploader.handleDropRejected(fileRejections);
    },
    [
      imagesAttachmentEnabled,
      videosAttachmentEnabled,
      filesAttachmentEnabled,
      images.handleDropRejected,
      videos.handleDropRejected,
      fileUploader.handleDropRejected,
    ]
  );

  useImperativeHandle(ref, () => ({ handleDropAccepted, handleDropRejected }), [
    handleDropAccepted,
    handleDropRejected,
  ]);

  const hasImages = images.images.length > 0 || images.inProgress.length > 0;
  const hasVideos = videos.videos.length > 0 || videos.inProgress.length > 0;
  const hasFiles =
    fileUploader.files.length > 0 || fileUploader.inProgress.length > 0;
  const hasLinks = linkUploader.links.length > 0;

  const attachments = userGeneratedContent.attachments ?? [];
  const storedOrder = mediaGroupOrder(attachments);
  const visibleGroups = resolveVisibleOrder(
    storedOrder,
    hasImages,
    hasVideos,
    hasFiles,
    hasLinks
  );

  const attachmentsRef = useRef<MediaGroup[]>(attachments);
  attachmentsRef.current = attachments;

  useEffect(() => {
    const currentAttachments = attachmentsRef.current;
    const current = mediaGroupOrder(currentAttachments);
    const next = resolveVisibleOrder(
      current,
      hasImages,
      hasVideos,
      hasFiles,
      hasLinks
    );

    if (next.join(',') !== current.join(',')) {
      const kept = currentAttachments.filter(
        (g) =>
          next.includes(g.type as MediaGroupType) ||
          (g.type !== 'images' &&
            g.type !== 'videos' &&
            g.type !== 'files' &&
            g.type !== 'links')
      );
      const nextAttachments = applyGroupOrder(kept, next);
      updateUserGeneratedContentFormState({
        attachments: nextAttachments.length > 0 ? nextAttachments : undefined,
      });
    }
  }, [hasImages, hasVideos, hasFiles, hasLinks, updateUserGeneratedContentFormState]);

  const handleImagesDelete = useCallback<typeof images.handleDelete>(
    (index) => {
      clearAllErrors();
      images.handleDelete(index);
    },
    [clearAllErrors, images.handleDelete]
  );
  const handleImagesReorder = useCallback<typeof images.handleReorder>(
    (imgs) => {
      clearAllErrors();
      images.handleReorder(imgs);
    },
    [clearAllErrors, images.handleReorder]
  );
  const handleVideosDelete = useCallback<typeof videos.handleDelete>(
    (index) => {
      clearAllErrors();
      videos.handleDelete(index);
    },
    [clearAllErrors, videos.handleDelete]
  );
  const handleVideosReorder = useCallback<typeof videos.handleReorder>(
    (vids) => {
      clearAllErrors();
      videos.handleReorder(vids);
    },
    [clearAllErrors, videos.handleReorder]
  );
  const handleFilesDelete = useCallback<typeof fileUploader.handleDelete>(
    (index) => {
      clearAllErrors();
      fileUploader.handleDelete(index);
    },
    [clearAllErrors, fileUploader.handleDelete]
  );
  const handleFilesReorder = useCallback<typeof fileUploader.handleReorder>(
    (files) => {
      clearAllErrors();
      fileUploader.handleReorder(files);
    },
    [clearAllErrors, fileUploader.handleReorder]
  );
  const handleLinksDelete = useCallback<typeof linkUploader.handleDelete>(
    (index) => {
      clearAllErrors();
      linkUploader.handleDelete(index);
    },
    [clearAllErrors, linkUploader.handleDelete]
  );
  const handleLinksReorder = useCallback<typeof linkUploader.handleReorder>(
    (next) => {
      clearAllErrors();
      linkUploader.handleReorder(next);
    },
    [clearAllErrors, linkUploader.handleReorder]
  );

  const handleGroupReorder = useCallback(
    (newOrder: MediaGroupType[]) => {
      updateUserGeneratedContentFormState({
        attachments: applyGroupOrder(attachmentsRef.current, newOrder),
      });
    },
    [updateUserGeneratedContentFormState]
  );

  return (
    <div className="post-media">
      {images.errors.map((err: UploaderError, index) => (
        <Alert
          key={err.id ?? err.description}
          description={err.description}
          iconName="error"
          size="compact"
          title={err.title}
          type="danger"
          onClose={() => images.dismissError(index)}
        />
      ))}
      {videos.errors.map((err: UploaderError, index) => (
        <Alert
          key={err.id ?? err.description}
          description={err.description}
          iconName="error"
          size="compact"
          title={err.title}
          type="danger"
          onClose={() => videos.dismissError(index)}
        />
      ))}
      {fileUploader.errors.map((err: UploaderError, index) => (
        <Alert
          key={err.id ?? err.description}
          description={err.description}
          iconName="error"
          size="compact"
          title={err.title}
          type="danger"
          onClose={() => fileUploader.dismissError(index)}
        />
      ))}
      {linkUploader.errors.map((err: UploaderError, index) => (
        <Alert
          key={err.id ?? err.description}
          description={err.description}
          iconName="error"
          size="compact"
          title={err.title}
          type="danger"
          onClose={() => linkUploader.dismissError(index)}
        />
      ))}
      <div className="post-media__dropzone" data-testid="post-media-dropzone">
        <input {...images.getInputProps()} />
        <input {...videos.getInputProps()} />
        <input {...fileUploader.getInputProps()} />

        {showAddToPostToolbar && (
          <div className="post-media__toolbar">
            <span className="post-media__toolbar-label text-subheading-bold">
              {t('user_generated_content.media.add_to_post')}
            </span>
            {imagesAttachmentEnabled && (
              <TooltipRoot>
                <TooltipTrigger asChild>
                  <IconButton
                    iconName="image"
                    size="compact"
                    onClick={images.open}
                    disabled={images.isAtLimit || images.isUploading}
                    aria-label={t('user_generated_content.media.images.add')}
                  />
                </TooltipTrigger>
                {/* py-2 = 8px vertical padding to match Figma; Solar's single-line tooltip defaults to py-1 (4px) */}
                <TooltipContent
                  className="py-2"
                  description={t('user_generated_content.media.images.add')}
                  sideOffset={5}
                />
              </TooltipRoot>
            )}
            {videosAttachmentEnabled && (
              <TooltipRoot>
                <TooltipTrigger asChild>
                  <IconButton
                    iconName="videocam"
                    size="compact"
                    onClick={videos.open}
                    disabled={videos.isAtLimit || videos.isUploading}
                    aria-label={t('user_generated_content.media.videos.add')}
                  />
                </TooltipTrigger>
                <TooltipContent
                  className="py-2"
                  description={t('user_generated_content.media.videos.add')}
                  sideOffset={5}
                />
              </TooltipRoot>
            )}
            {filesAttachmentEnabled && (
              <TooltipRoot>
                <TooltipTrigger asChild>
                  <IconButton
                    iconName="attach_file"
                    size="compact"
                    onClick={fileUploader.open}
                    disabled={
                      fileUploader.isAtLimit || fileUploader.isUploading
                    }
                    aria-label={t('user_generated_content.media.files.add')}
                  />
                </TooltipTrigger>
                <TooltipContent
                  className="py-2"
                  description={t('user_generated_content.media.files.add')}
                  sideOffset={5}
                />
              </TooltipRoot>
            )}
            {linksAttachmentEnabled && (
              <TooltipRoot>
                <TooltipTrigger asChild>
                  <IconButton
                    iconName="link"
                    size="compact"
                    onClick={linkUploader.openInput}
                    disabled={
                      linkUploader.isAtLimit ||
                      linkUploader.isInputOpen ||
                      linkUploader.isFetching
                    }
                    aria-label={t('user_generated_content.media.links.add')}
                  />
                </TooltipTrigger>
                <TooltipContent
                  className="py-2"
                  description={t('user_generated_content.media.links.add')}
                  sideOffset={5}
                />
              </TooltipRoot>
            )}
          </div>
        )}

        {linkUploader.isInputOpen && (
          <LinkInput
            isFetching={linkUploader.isFetching}
            onSubmit={linkUploader.handleAdd}
            onClose={linkUploader.closeInput}
          />
        )}

        {visibleGroups.length > 0 && (
          <SortableList
            items={visibleGroups}
            getId={(g) => g}
            onReorder={handleGroupReorder}
          >
            {visibleGroups.map((groupType) => (
              <SortableGroupWrapper key={groupType} id={groupType}>
                {(dragHandleProps, isDragging) => {
                  if (groupType === 'images')
                    return (
                      <Images
                        images={images.images}
                        inProgress={images.inProgress}
                        totalCount={images.totalCount}
                        limitError={images.limitError}
                        onDelete={handleImagesDelete}
                        onReorder={handleImagesReorder}
                        dragHandleProps={dragHandleProps}
                        isDragging={isDragging}
                      />
                    );
                  if (groupType === 'files')
                    return (
                      <Files
                        files={fileUploader.files}
                        inProgress={fileUploader.inProgress}
                        totalCount={fileUploader.totalCount}
                        limitError={fileUploader.limitError}
                        onDelete={handleFilesDelete}
                        onReorder={handleFilesReorder}
                        dragHandleProps={dragHandleProps}
                        isDragging={isDragging}
                      />
                    );
                  if (groupType === 'links')
                    return (
                      <Links
                        links={linkUploader.links}
                        totalCount={linkUploader.totalCount}
                        onDelete={handleLinksDelete}
                        onReorder={handleLinksReorder}
                        dragHandleProps={dragHandleProps}
                        isDragging={isDragging}
                      />
                    );
                  return (
                    <Videos
                      videos={videos.videos}
                      inProgress={videos.inProgress}
                      totalCount={videos.totalCount}
                      limitError={videos.limitError}
                      onDelete={handleVideosDelete}
                      onReorder={handleVideosReorder}
                      dragHandleProps={dragHandleProps}
                      isDragging={isDragging}
                    />
                  );
                }}
              </SortableGroupWrapper>
            ))}
          </SortableList>
        )}
      </div>
    </div>
  );
});

export default PostMedia;
